import { AnalyticIdentifier, formatMonthYearPeriod } from '@common/neo.tax';
import {
  Button,
  FormStickyHeaderMemo,
  Icon,
  Loading,
  PageTitle,
  Stack,
  TwoColumnLayoutMemo,
  Typography,
  colors,
} from '@ui-components/neo.tax';
import { ReactComponent as CalendarIcon } from '@ui-components/neo.tax/dist/icons/calendar.svg';
import { ReactComponent as ClockIcon } from '@ui-components/neo.tax/dist/icons/clock.svg';
import { ReactComponent as DownloadIcon } from '@ui-components/neo.tax/dist/icons/download.svg';
import { ReactComponent as LockClosedIcon } from '@ui-components/neo.tax/dist/icons/lock-closed.svg';

import { useAscContext } from '@neo.tax/asc/context';
import {
  BackgroundOperationName,
  PageLayout,
  RightColumnCard,
  useAnalyticId,
  useBackgroundOperations,
  useCurrentUserScopeForCurrentCompany,
} from '@neo.tax/shared';

import { AscPeriodLockCard } from '../asc-period-lock-card';
import { AscSnapshotList } from './AscSnapshotList';
import { useGenerateAscReport, useGetDocumentEventSignedUrl, useGetLatestAscReportDocumentEvent } from './hooks';

const formatGeneratedAt = (createdAt: string | Date): string =>
  new Date(createdAt).toLocaleString('en-US', { dateStyle: 'long', timeStyle: 'short' });

export const ExportAscForm = (): React.ReactElement => {
  const { generateAscReport, loading } = useGenerateAscReport();
  const { limitAccessToFinancialData } = useCurrentUserScopeForCurrentCompany();
  const { fiscalYearId, period, periodName } = useAscContext();
  const generateAscReportId = useAnalyticId(AnalyticIdentifier.ENTERPRISE_GENERATE_ASC_REPORT_BUTTON);

  const latestReportResult = useGetLatestAscReportDocumentEvent(fiscalYearId, periodName);
  const latestReportEvent = latestReportResult.status === 'loaded' ? latestReportResult.documentEvent : null;
  const { getDocumentEventSignedUrl, loading: signedUrlLoading } = useGetDocumentEventSignedUrl();

  // Heuristic guard: block Generate/Regenerate if any in-flight
  // GENERATE_ASC_REPORT_OPERATION exists for the company. Period-level
  // discrimination isn't possible today because the operation row doesn't
  // carry fiscalYearId/periodName — this is intentionally coarse and prevents
  // duplicate submissions across tabs / after a refresh.
  const backgroundOps = useBackgroundOperations();
  const isAscGenerationInFlight =
    !backgroundOps.loading &&
    backgroundOps.backgroundOperations.some(
      (op) => op.name === BackgroundOperationName.GenerateAscReportOperation && op.progressPercent < 100,
    );

  const handleExport = async (): Promise<void> => {
    await generateAscReport({ variables: { fiscalYearId, periodName } });
  };

  const handleDownload = async (): Promise<void> => {
    if (!latestReportEvent) {
      return;
    }
    const { data } = await getDocumentEventSignedUrl({ variables: { documentEventId: latestReportEvent.id } });
    if (data) {
      window.open(data.getDocumentEventSignedUrl, '_blank');
    }
  };

  const { end, start } = formatMonthYearPeriod(period);

  return (
    <PageLayout
      title={
        <PageTitle>
          Export |{' '}
          <Typography color={colors.blue[600]} fontSize="inherit" fontWeight="inherit">
            ASC 350-40 Documents
          </Typography>
        </PageTitle>
      }
      subtitle="Download complete list of projects, allocation by person, and audit documentation."
      pageHeaderBorder
    >
      <Stack alignItems="start" justifyContent="start" direction="column" gap={5}>
        <TwoColumnLayoutMemo
          leftColumn={
            <FormStickyHeaderMemo
              title="Lock period"
              subtitle="Exports keep working while locked"
              icon={<LockClosedIcon />}
            />
          }
        >
          <RightColumnCard>
            <AscPeriodLockCard />
          </RightColumnCard>
        </TwoColumnLayoutMemo>
        <TwoColumnLayoutMemo
          leftColumn={
            <FormStickyHeaderMemo
              title="Export ASC 350-40 documents"
              subtitle={`Selected period: ${start} - ${end}`}
              icon={<CalendarIcon />}
            />
          }
        >
          <RightColumnCard>
            <Stack direction="column" gap={5} fullWidth>
              <Typography fontSize="sm" color={colors.gray[600]}>
                ASC 350-40 documents for the period{' '}
                <Typography fontSize="inherit" color="inherit" fontWeight="medium">{`${start} - ${end}`}</Typography>
              </Typography>
              <Stack direction="column" alignItems="end" gap={1}>
                {latestReportResult.status === 'loading' || backgroundOps.loading ? (
                  <Loading height={80} />
                ) : (
                  <>
                    {latestReportEvent && (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Icon node={<ClockIcon />} size={4} colorVariant="none" />
                        <Typography fontSize="xs" color={colors.gray[600]}>
                          Last generated on {formatGeneratedAt(latestReportEvent.createdAt)}
                        </Typography>
                      </Stack>
                    )}
                    <Stack direction="row" gap={2}>
                      <Button
                        size="base"
                        variant={latestReportEvent ? 'white' : 'primary'}
                        rounded="regular"
                        startIcon={latestReportEvent ? undefined : <DownloadIcon />}
                        disabled={loading || isAscGenerationInFlight}
                        onClick={handleExport}
                        id={generateAscReportId}
                      >
                        {loading ? 'Requesting..' : latestReportEvent ? 'Regenerate' : 'Generate'}
                      </Button>
                      {latestReportEvent && (
                        <Button
                          size="base"
                          variant="primary"
                          rounded="regular"
                          startIcon={<DownloadIcon />}
                          disabled={signedUrlLoading}
                          onClick={handleDownload}
                        >
                          {signedUrlLoading ? 'Downloading…' : 'Download'}
                        </Button>
                      )}
                    </Stack>
                    {isAscGenerationInFlight && (
                      <Typography fontSize="xs" color={colors.primary[700]}>
                        Documents are being generated.
                      </Typography>
                    )}
                    <Typography fontSize="xs" color={colors.gray[600]}>
                      {latestReportEvent
                        ? 'Regenerated documents will be sent to your email address.'
                        : 'Documents will be sent to your email address.'}
                    </Typography>
                  </>
                )}
              </Stack>
            </Stack>
          </RightColumnCard>
        </TwoColumnLayoutMemo>
        {!limitAccessToFinancialData && (
          <TwoColumnLayoutMemo
            leftColumn={
              <FormStickyHeaderMemo
                title="Previous Documents"
                subtitle="Snapshot of the ASC 350-40 documents in the previous period"
                icon={<CalendarIcon />}
              />
            }
          >
            <RightColumnCard>
              <Stack direction="column" gap={4} fullWidth>
                <AscSnapshotList />
              </Stack>
            </RightColumnCard>
          </TwoColumnLayoutMemo>
        )}
      </Stack>
    </PageLayout>
  );
};
